import { CloudUpload, FileText } from "lucide-react";
import {
  Text,
  createStyles,
  Group,
  MantineTheme,
  useMantineTheme,
  DefaultMantineColor,
  Button,
} from "@mantine/core";
import { Dropzone, DropzoneStatus } from "@mantine/dropzone";
import React, { useEffect, useRef } from "react";

const useStyles = createStyles((theme) => ({
  wrapper: {
    position: "relative",
    marginBottom: 30,
  },

  dropzone: {
    borderWidth: 1,
    paddingBottom: 75,
  },

  icon: {
    color:
      theme.colorScheme === "dark"
        ? theme.colors.dark[3]
        : theme.colors.gray[4],
  },

  control: {
    position: "absolute",
    width: 150,
    left: "calc(50% - 75px)",
    bottom: 20,
  },
}));

function getActiveColor(status: DropzoneStatus, theme: MantineTheme): string {
  if (status.accepted) {
    return theme.colors[theme.primaryColor as DefaultMantineColor][6];
  }

  if (status.rejected) {
    return theme.colors.red[6];
  }

  return theme.black;
}

//   const getStatus = (status: DropzoneStatus): string => {
//     if (status.accepted) {
//       return "Drop files here";
//     }

//     if (status.rejected) {
//       return "Pdf file less than 30mb";
//     }

//     return "Upload resume";
//   };

interface FileError {
  message: string;
  code: string;
}
export interface FileRejection {
  file: File;
  errors: FileError[];
}

type FileUploadBodyProps = {
  status: DropzoneStatus;
  theme: MantineTheme;
  getStatus: (status: DropzoneStatus) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  description: any;
  file?: File;
};

const FileUploadBody: React.FC<FileUploadBodyProps> = (
  { status, theme, getStatus, description, file } // eslint-disable-next-line arrow-body-style
) => {
  return !file ? (
    <Group
      position="center"
      direction="column"
      align="center"
      style={{ pointerEvents: "none" }}
    >
      <CloudUpload size={50} color={getActiveColor(status, theme)} />
      <Text
        align="center"
        weight={700}
        size="xl"
        // mt="xl"
        sx={{ color: getActiveColor(status, theme) }}
      >
        {getStatus(status)}
      </Text>
      {description}
    </Group>
  ) : (
    <Group
      position="center"
      direction="column"
      align="center"
      style={{ pointerEvents: "none" }}
    >
      <FileText size={50} color={getActiveColor(status, theme)} />
      <Text
        align="center"
        weight={700}
        size="xl"
        // mt="xl"
        sx={{ color: getActiveColor(status, theme) }}
      >
        {file?.name}
      </Text>
    </Group>
  );
};

type FileUploadProps = {
  accept: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  description: any;
  file?: File;
  getStatus: (status: DropzoneStatus) => string;
  isLoading: boolean;
  onDrop: (files: File[]) => void;
  onRemove: () => void;
  onReject?: (fileRejections: FileRejection[]) => void;
  maxSize: number;
  className?: string;
};

const FileUpload: React.FC<FileUploadProps> = ({
  accept,
  file,
  description,
  getStatus,
  isLoading,
  onDrop,
  onRemove,
  onReject,
  maxSize,
  className,
}) => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const openRef = useRef<() => void>(null);

  useEffect(() => {
    document
      .getElementById("payslips-modal-input-wrapper")
      ?.children[0].firstElementChild?.setAttribute(
        "id",
        "payslips-modal-file-input"
      );
  }, []);

  return (
    <div
      className={
        className ? `${classes.wrapper} ${className}` : classes.wrapper
      }
      id="payslips-modal-input-wrapper"
    >
      <Dropzone
        loading={isLoading}
        openRef={openRef}
        onDrop={(files) => onDrop(files)}
        className={classes.dropzone}
        radius="md"
        accept={accept}
        maxSize={maxSize}
        onReject={onReject}
      >
        {(status) => (
          <div style={{ pointerEvents: "none" }}>
            <FileUploadBody
              status={status}
              theme={theme}
              getStatus={getStatus}
              description={description}
              file={file}
            />
          </div>
        )}
      </Dropzone>
      {!file ? (
        <Button
          className={classes.control}
          size="md"
          radius="sm"
          color="jem-theme"
          onClick={() => {
            if (openRef && openRef.current) {
              openRef.current();
            }
          }}
        >
          Select File
        </Button>
      ) : (
        <Button
          className={classes.control}
          variant="outline"
          color="var(--primary-primary-base)"
          style={{
            color: "var(--primary-primary-base)",
            borderColor: "var(--primary-primary-base)",
            borderWidth: "1px",
            fontSize: "14px",
            fontWeight: 500,
          }}
          size="md"
          radius="sm"
          onClick={() => {
            onRemove();
          }}
        >
          Remove File
        </Button>
      )}
    </div>
  );
};

export default FileUpload;
